import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateContactMutation, ContactPropertiesInput } from '@/lib/graphql/types-and-hooks';
import { isLovablePreview } from '@/lib/utils/environment';
import { toast } from 'sonner';
import { debounce } from 'lodash';
import { useCanMutate, assertCanMutate } from '@/lib/rbac';

/**
 * Hook for updating contact properties with backend integration
 * Handles validation, debouncing, and Lovable Preview mode
 */
export const useContactUpdate = (contactId: string, onSuccess?: () => void) => {
  const { t } = useTranslation();
  const canMutate = useCanMutate();
  const [updateContact, { loading, error }] = useUpdateContactMutation({
    // No refetchQueries — that would reset the windowed grid to the top. The grid now
    // fetches pages with `no-cache` (useWindowedConnection), so mutation responses no
    // longer propagate into it via the normalized cache. PeoplePage instead reflects
    // edits through its cellEdits overlay + the PersonDetailSheet onFieldUpdate callback.
  });

  /**
   * Update contact properties in backend
   * Optimistic updates handled by Apollo cache
   */
  const updateContactProperties = useCallback(
    async (properties: Partial<ContactPropertiesInput>) => {
      if (!assertCanMutate(canMutate)) return { success: false };
      // In Lovable Preview mode, just show success toast
      if (isLovablePreview()) {
        toast.success(t('mutations.contactUpdatedPreview'));
        return { success: true };
      }

      try {
        const result = await updateContact({
          variables: {
            input: {
              id: contactId,
              properties,
            },
          },
        });

        if (result.data?.updateContact) {
          toast.success(t('mutations.contactUpdated'));
          onSuccess?.();
          return { success: true, data: result.data.updateContact };
        }

        return { success: false };
      } catch (err) {
        console.error('Failed to update contact:', err);
        // Extract error message from Apollo error or use default
        const errorMessage = (err as any)?.message || t('mutations.contactUpdateFailed');
        toast.error(errorMessage);
        return { success: false, error: err };
      }
    },
    [contactId, updateContact, onSuccess, canMutate, t]
  );

  /**
   * Debounced update for text fields (500ms delay)
   * Prevents excessive API calls while user is typing
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdate = useCallback(
    debounce((properties: Partial<ContactPropertiesInput>) => {
      updateContactProperties(properties);
    }, 500),
    [updateContactProperties]
  );

  /**
   * Update name (firstName + lastName)
   */
  const updateName = useCallback(
    (fullName: string) => {
      const parts = fullName.trim().split(' ');
      const firstName = parts[0] || '';
      const lastName = parts.slice(1).join(' ') || '';

      return debouncedUpdate({
        firstName,
        lastName,
      });
    },
    [debouncedUpdate]
  );

  /**
   * Update email (primaryEmail)
   */
  const updateEmail = useCallback(
    (email: string) => {
      // Validation happens in the component layer
      return updateContactProperties({
        primaryEmail: email,
      });
    },
    [updateContactProperties]
  );

  /**
   * Update additional emails (primaryEmail + additionalEmails)
   * First email becomes the primary email
   * Remaining emails go to additionalEmails
   */
  const updateAdditionalEmails = useCallback(
    (emails: string[], currentPrimaryEmail?: string | null, currentAdditionalEmails?: string[] | null) => {
      const [primaryEmail, ...additionalEmails] = emails;

      // Build update object - only include fields that changed
      const update: Partial<ContactPropertiesInput> = {};

      // Check if primary email changed
      const newPrimaryEmail = primaryEmail || null;
      if (newPrimaryEmail !== currentPrimaryEmail) {
        update.primaryEmail = newPrimaryEmail as any;
      }

      // Check if additional emails changed
      const newAdditionalEmails = additionalEmails.length > 0 ? additionalEmails : null;
      const currentAdditionalEmailsNormalized = currentAdditionalEmails && currentAdditionalEmails.length > 0 ? currentAdditionalEmails : null;

      // Compare arrays
      const additionalEmailsChanged = JSON.stringify(newAdditionalEmails) !== JSON.stringify(currentAdditionalEmailsNormalized);
      if (additionalEmailsChanged) {
        update.additionalEmails = newAdditionalEmails as any;
      }

      // Only send update if something changed
      if (Object.keys(update).length === 0) {
        console.log('[updateAdditionalEmails] No changes detected, skipping update');
        return Promise.resolve({ success: true });
      }

      return debouncedUpdate(update);
    },
    [debouncedUpdate]
  );

  /**
   * Update phone number
   */
  const updatePhone = useCallback(
    (phone: string) => {
      return debouncedUpdate({ phone });
    },
    [debouncedUpdate]
  );

  /**
   * Update job title
   */
  const updateJobTitle = useCallback(
    (jobTitle: string) => {
      return debouncedUpdate({ jobTitle });
    },
    [debouncedUpdate]
  );

  /**
   * Update LinkedIn URL
   */
  const updateLinkedIn = useCallback(
    (linkedinProfileUrl: string) => {
      return updateContactProperties({ linkedinProfileUrl });
    },
    [updateContactProperties]
  );

  /**
   * Update location (city, state, country)
   */
  const updateLocation = useCallback(
    (location: { city?: string; state?: string; country?: string }) => {
      return debouncedUpdate(location);
    },
    [debouncedUpdate]
  );

  /**
   * Update company association
   * Updates the contact's accountIds to associate with a company
   */
  const updateCompany = useCallback(
    async (accountId: string) => {
      if (isLovablePreview()) {
        toast.success(t('mutations.companyUpdatedPreview'));
        return { success: true };
      }

      return updateContactProperties({
        accountIds: [accountId],
      });
    },
    [updateContactProperties, t]
  );

  /**
   * Flush any pending debounced updates immediately.
   * Call this before closing the sheet to prevent lost edits.
   */
  const flush = useCallback(() => {
    debouncedUpdate.flush();
  }, [debouncedUpdate]);

  return {
    updateName,
    updateEmail,
    updateAdditionalEmails,
    updatePhone,
    updateJobTitle,
    updateLinkedIn,
    updateLocation,
    updateCompany,
    flush,
    loading,
    error,
  };
};
